import { get, post } from '@/services/api'
import type {
  LoginCredentials,
  AuthResponse,
  ForgotPasswordResponse,
  ResetPasswordData,
  ResetPasswordResponse
} from '../types'

const ensureCsrfCookie = async () => {
  const target = import.meta.env.VITE_API_TARGET || 'http://localhost:8000'
  await get(`${target}/sanctum/csrf-cookie`, { withCredentials: true })
}

/**
 * Authentication Service
 * Handles all API calls related to user authentication.
 */
export const authService = {
  /**
   * Login with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    await ensureCsrfCookie()

    const { data } = await post<AuthResponse>('/login', credentials)
    return data
  },

  /**
   * Get current user profile
   */
  async getProfile(): Promise<AuthResponse> {
    const { data } = await get<AuthResponse>('/me')
    return data
  },

  /**
   * Send password reset link
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    await ensureCsrfCookie()

    const { data } = await post<ForgotPasswordResponse>('/forgot-password', { email })
    return data
  },

  /**
   * Reset user password
   */
  async resetPassword(data: ResetPasswordData): Promise<ResetPasswordResponse> {
    await ensureCsrfCookie()

    const { data: responseData } = await post<ResetPasswordResponse>('/reset-password', data)
    return responseData
  },

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    await ensureCsrfCookie()
    await post('/logout')
  },

  /**
   * Set user password (for authenticated users)
   */
  async setPassword(
    password: string,
    password_confirmation: string
  ): Promise<{ result: string; message: string }> {
    await ensureCsrfCookie()
    const { data } = await post<{ result: string; message: string }>('/set-password', {
      password,
      password_confirmation
    })
    return data
  },

  /**
   * Impersonate a user
   */
  async impersonate(userId: number | string): Promise<AuthResponse> {
    const { data } = await post<AuthResponse>(`/impersonate/${userId}`)
    return data
  },

  /**
   * Leave impersonation
   */
  async leaveImpersonation(): Promise<{ result: string; message: string }> {
    const { data } = await post<{ result: string; message: string }>('/impersonate-leave')
    return data
  }
}
